/**
 * InductionReview - Review all answers before final submission
 * Route: /i/:token/review
 *
 * Visitor sees a summary of their answers + signature preview,
 * then confirms and submits — which queues the submission for
 * site manager sign-off before the pass is issued.
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { submitInduction, parseRpcError } from '@shared/utils/publicRpc';
import { useInviteToken, buildInvitePath } from '@shared/utils/inviteToken';

export default function InductionReview() {
  const token = useInviteToken();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const state = location.state;

  // Guard: if someone navigates here directly without form state, send them back
  useEffect(() => {
    if (!state?.answers) {
      navigate(buildInvitePath(token, 'induction'), { replace: true });
    }
  }, [state, token, navigate]);

  if (!state?.answers) return null;

  const { answers, signatureDataUrl, brief, invite } = state;
  const sections = brief?.content?.sections || [];

  const handleConfirmSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const result = await submitInduction(token, answers, signatureDataUrl);

      if (result?.success) {
        navigate(buildInvitePath(token, 'complete'), {
          state: {
            reference: result.reference,
            submitted_at: result.submitted_at,
          },
          replace: true,
        });
      } else {
        setError('Submission failed. Please try again.');
      }
    } catch (err) {
      setError(parseRpcError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = () => {
    navigate(buildInvitePath(token, 'induction'), {
      state: { answers, signatureDataUrl },
    });
  };

  const renderAnswer = (section, answer) => {
    if (!answer) return <span className="text-gray-400 italic">No answer</span>;
    if (answer.type === 'checkbox') {
      return answer.answer === true ? (
        <span className="text-green-700 font-medium flex items-center gap-1">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Confirmed
        </span>
      ) : (
        <span className="text-red-600 italic">Not confirmed</span>
      );
    }
    if (answer.answer) return <span className="text-gray-900">{answer.answer}</span>;
    return <span className="text-gray-400 italic">No answer provided</span>;
  };

  return (
    <div className="space-y-6">

      {/* ── Header ─────────────────────────────────────────── */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
            <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Review Your Submission</h1>
            <p className="mt-1 text-sm text-gray-600">
              Please check your answers carefully. Once submitted, the site manager will review them
              before issuing your access pass.
            </p>
          </div>
        </div>
      </div>

      {/* ── Progress indicator ─────────────────────────────── */}
      <div className="flex items-center gap-2 px-1">
        {['Welcome', 'Induction Brief', 'Review', 'Pending Approval'].map((step, i) => (
          <div key={step} className="flex items-center gap-2 flex-1 last:flex-initial">
            <div className={`flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold
              ${i < 2 ? 'bg-green-500 text-white' : ''}
              ${i === 2 ? 'bg-blue-600 text-white ring-2 ring-blue-200' : ''}
              ${i > 2 ? 'bg-gray-200 text-gray-400' : ''}
            `}>
              {i < 2 ? (
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : i + 1}
            </div>
            <span className={`text-xs font-medium truncate
              ${i === 2 ? 'text-blue-700' : 'text-gray-400'}
            `}>{step}</span>
            {i < 3 && <div className="flex-1 h-px bg-gray-200 min-w-[1rem]" />}
          </div>
        ))}
      </div>

      {/* ── Submitting as ──────────────────────────────────── */}
      {invite && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
            Submitting as
          </h2>
          <div className="flex flex-wrap gap-6">
            <div>
              <p className="text-xs text-gray-500">Name</p>
              <p className="text-sm font-semibold text-gray-900">{invite.person_name}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Email</p>
              <p className="text-sm font-semibold text-gray-900">{invite.person_email}</p>
            </div>
            {invite.person_company && (
              <div>
                <p className="text-xs text-gray-500">Company</p>
                <p className="text-sm font-semibold text-gray-900">{invite.person_company}</p>
              </div>
            )}
            {invite.person_phone && (
              <div>
                <p className="text-xs text-gray-500">Phone</p>
                <p className="text-sm font-semibold text-gray-900">{invite.person_phone}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Brief title ────────────────────────────────────── */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">
          Induction Brief
        </h2>
        <p className="text-lg font-semibold text-gray-900">{brief?.title || 'Site Induction'}</p>
      </div>

      {/* ── Answers summary ────────────────────────────────── */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
            Your Answers
          </h2>
        </div>

        {sections.filter(s => s.type !== 'info').length === 0 ? (
          <p className="px-6 py-4 text-sm text-gray-500 italic">No questions in this brief.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {sections
              .filter(s => s.type !== 'info')
              .map((section, idx) => {
                const key = section.id;
                const answer = answers[key];
                return (
                  <div key={key || idx} className="px-6 py-4 flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-500 mb-1">
                        {section.label || section.title}
                        {section.required && <span className="text-red-500 ml-1">*</span>}
                      </p>
                      <div className="text-sm">{renderAnswer(section, answer)}</div>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* ── Signature preview ──────────────────────────────── */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">
          Signature
        </h2>
        {signatureDataUrl ? (
          <>
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 inline-block">
              <img
                src={signatureDataUrl}
                alt="Your signature"
                className="max-h-28 max-w-full"
              />
            </div>
            <p className="mt-2 text-xs text-gray-500">
              By submitting, you confirm this is your digital signature.
            </p>
          </>
        ) : (
          <p className="text-sm text-red-600">No signature captured — please go back and sign.</p>
        )}
      </div>

      {/* ── Declaration ────────────────────────────────────── */}
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-5">
        <div className="flex items-start gap-3">
          <svg className="flex-shrink-0 h-5 w-5 text-amber-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd" />
          </svg>
          <div>
            <p className="text-sm font-semibold text-amber-800 mb-1">Declaration</p>
            <p className="text-sm text-amber-700">
              By clicking <strong>Confirm &amp; Submit</strong>, you confirm that all information
              provided is accurate and that you have read and understood the full site induction brief.
              Your submission will be sent to the site manager for review before your pass is issued.
              You will receive an email once your pass has been approved.
            </p>
          </div>
        </div>
      </div>

      {/* ── Error ──────────────────────────────────────────── */}
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      )}

      {/* ── Actions ────────────────────────────────────────── */}
      <div className="flex items-center justify-between bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <button
          type="button"
          onClick={handleEdit}
          disabled={submitting}
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 transition-colors"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Edit Answers
        </button>

        <button
          type="button"
          onClick={handleConfirmSubmit}
          disabled={submitting || !signatureDataUrl}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-base font-semibold text-white
            bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500
            disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-sm"
        >
          {submitting ? (
            <>
              <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Submitting...
            </>
          ) : (
            <>
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Confirm &amp; Submit
            </>
          )}
        </button>
      </div>

    </div>
  );
}
